<template>
    <div>
        <span class="text-lg">
            Trạng thái vận hành của chặng hàng
        </span>
        <div class="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-8">
            <analytics-card
                :text="$t('labels.feasible')"
                :value="$formatters.formatCurrency(dataAnalytics.totalFeasible)"
                color="success"
                increase
                @click="getItems('feasible')" />
            <analytics-card
                :text="$t('labels.cannotExecute')"
                :value="$formatters.formatCurrency(dataAnalytics.totalCannotExecute)"
                color="accent"
                increase
                @click="getItems('cannotExecute')" />
            <analytics-card
                :text="$t('labels.challengingExecution')"
                :value="$formatters.formatCurrency(dataAnalytics.totalChallengingExecution)"
                color="error"
                increase
                @click="getItems('challengingExecution')" />
        </div>
        <div class="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-8">
            <div class="sm:col-start-3">
                <analytics-card
                    :text="$t('labels.waitTimeOccurred')"
                    :value="$formatters.formatCurrency(dataAnalytics.totalWaitTimeOccurred)"
                    color="error"
                    increase
                    @click="getItems('waitTimeOccurred')" />
            </div>
        </div>
    </div>
    <analytics-modal
        v-model:visible="visible"
        :headers="headers"
        :loading="loading"
        :items="items" />
</template>
<script lang="ts" setup>
import AnalyticsCard from '../analytics-card.vue'
import AnalyticsModal from '../analytics-modal.vue'
import { $api, $lodash } from '#imports'

import Status from '~/components/status.vue'

const dataAnalytics = ref<{
  totalFeasible: number
  totalCannotExecute: number
  totalChallengingExecution: number
  totalWaitTimeOccurred: number
}>({
  totalFeasible: 0,
  totalCannotExecute: 0,
  totalChallengingExecution: 0,
  totalWaitTimeOccurred: 0
})

onMounted(async () => {
  const api = $url.dashboard.operationShipmentLeg.getCargoSegmentAlertStatus
  const data: any = await $api(api)
  const resData: any = $lodash.get(data, 'data.value')
  if (resData?.success) {
    dataAnalytics.value = resData.data
  }
})

const { $formatters } = useNuxtApp()

const items = ref<Array<any>>([])
const store = $store()

const loading = ref(false)
const visible = ref(false)
const getItems = async (type: string) => {
  try {
    loading.value = true
    visible.value = true
    const api = $url.dashboard.operationShipmentLeg.getDataCargoSegmentAlertStatus
    const data: any = await $api(api, { uriParams: { TYPE: type } })
    const resData: any = $lodash.get(data, 'data.value')
    if (resData?.success) {
      items.value = resData.data
    }
    loading.value = false
  } catch (e: any) {
    loading.value = false
    visible.value = false
  }
}

const headers = ref([
  {
    field: 'code',
    headerName: $t('labels.code'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'containerNo',
    headerName: $t('labels.containerNo'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'orderCode',
    headerName: $t('labels.order'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'estimateStartTime.departureTime',
    headerName: 'Thời gian dự kiến khởi hành',
    valueGetter: (params: any) => {
      const data = params.data.startAt
      if (!data) {
        return ''
      }
      return $formatters.datetime(data)
    }
  },
  {
    headerName: 'Thời gian thực hiện chặng hàng',
    valueGetter: (params: any) => {
      const timeIn = params.data?.timeIn
      const timeOut = params.data?.timeOut
      return $formatters.datetime(timeIn) + ' - ' + $formatters.datetime(timeOut)
    }
  },
  {
    field: 'estimateStartTime.departureLocationName',
    headerName: 'Địa điểm khởi hành'
  },
  {
    field: 'journeyName',
    headerName: $t('labels.journey2'),
    valueGetter: (params: any) => {
      const stages = params.data?.stages
      const names = stages.map((item: any) => item.nodeLocationName)
      return names.join(' - ')
    }
  },
  {
    field: 'vehicleLicensePlate',
    headerName: $t('labels.vehicle'),
    valueGetter: (params: any) => {
      const vehicleLicensePlate = params.data?.vehicleLicensePlate
      const vehicleTempLicensePlate = params.data?.vehicleTempLicensePlate
      return vehicleLicensePlate || vehicleTempLicensePlate || ''
    }
  },
  {
    field: 'journeyIndex',
    headerName: $t('labels.journeyIndex')
  },
  {
    field: 'partnerName',
    headerName: $t('labels.fleetVehicle')
  },
  {
    field: 'transportation',
    sortable: true,
    headerName: $t('labels.transportation'),
    valueGetter: (params: any) => {
      const transportation = params?.data.orderData?.transportation
      if (transportation === 'CONTAINER') {
        return $t('labels.containerTruck')
      }
      if (transportation === 'NON_CONTAINER') {
        return $t('labels.nonContainerTruck')
      }
    }
  },
  {
    field: 'contCategory',
    headerName: $t('labels.cont'),
    valueGetter: (params: any) => {
      const contCategory = params.data?.orderData?.contCategory
      if (contCategory) {
        if (contCategory === 20) {
          return $t('labels.20f')
        }
        if (contCategory === 40) {
          return $t('labels.40f')
        }
        if (contCategory === 45) {
          return $t('labels.45f')
        }
      }
    }
  },
  {
    field: 'mergeContainer',
    headerName: $t('labels.mergeContainer'),
    valueGetter: (params: any) => {
      const mergeContainer = params?.data?.orderData?.mergeContainer
      if (mergeContainer) {
        return $t('labels.haveMergeContainer')
      }
      return $t('labels.notMergeContainer')
    }
  },
  {
    field: 'headContainer',
    headerName: $t('labels.headContainer'),
    valueGetter: (params: any) => {
      const headContainer = params?.data?.orderData?.headContainer
      if (headContainer) {
        return $t('labels.true')
      }
      return $t('labels.false')
    }
  },
  {
    field: 'commodityName',
    headerName: $t('labels.commodity'),
    valueGetter: (params: any) => {
      const commodityName = params?.data?.orderData?.commodityName
      const commodityCategory = params?.data?.orderData?.commodityCategory
      if (commodityName && commodityCategory) {
        return `${commodityName} - ${commodityCategory}`
      }
      if (commodityName) {
        return commodityName
      }
      return ''
    }
  },
  {
    field: 'weightCommodity',
    headerName: $t('labels.weightCommodityByKG1'),
    valueGetter: (params: any) => {
      return $formatters.formatNumber(params.data.orderData?.weightCommodity)
    }
  },
  {
    field: 'status',
    headerName: $t('labels.status'),
    cellRenderer: Status,
    cellRendererParams: {
      module: 'journeyStatus',
      label: '',
      keyStatus: 'status'
    }
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.createdAt)
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.updatedAt)
    }
  }
])
</script>
